import React, { ReactElement, ReactNode } from "react";

import Tooltip from "antd/es/tooltip";
import { defineMessage, useIntl } from "react-intl";

import styles from "./PeopleOverflowTooltip.module.scss";

interface PeopleOverflowTooltipProps {
  children: ReactElement;
  people: ReadonlyArray<{ id: number | string; name?: ReactNode }>;
}

// Cap how many names are listed so a large overflow (e.g. "+99 more") can't render a wall of text.
const MAX_VISIBLE_NAMES = 20;

const moreMessage = defineMessage({
  id: "peopleOverflowTooltip.more",
  defaultMessage: "…and {count, plural, one {# more} other {# more}}",
  description: "Trailing label when the people tooltip lists more names than it shows",
});

// Wraps an overflow trigger ("+ N more") with a tooltip listing the hidden people's names horizontally.
const PeopleOverflowTooltip = ({ children, people }: PeopleOverflowTooltipProps) => {
  const intl = useIntl();
  const visiblePeople = people.slice(0, MAX_VISIBLE_NAMES);
  const remainingCount = people.length - visiblePeople.length;

  return (
    <Tooltip
      overlayClassName={styles.overlay}
      placement="left"
      title={
        <div className={styles.names}>
          {visiblePeople.map((person) => (
            <span className={styles.name} key={person.id}>
              {person.name}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className={styles.more}>
              {intl.formatMessage(moreMessage, { count: remainingCount })}
            </span>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};

export default PeopleOverflowTooltip;
